import { createRootRoute, Outlet } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { AppToaster } from "../components/ui/toaster";

import { useSession } from "../features/auth/session";
import { SessionBoundary } from "../features/auth/session-boundary";
import { ThemeController } from "../features/theme/ThemeController";
import { ForegroundPushToasts } from "../features/notifications/ForegroundPushToasts";
import { PushRegistrationProvider } from "../features/notifications/use-push-registration";

/**
 * The Toaster is mounted once here so every route can report the outcome of a
 * write without mounting its own. sonner ships with this template; an app that
 * never imports it gives silent feedback on actions users are accountable for,
 * and a silent success gets clicked twice.
 *
 * SessionBoundary is here for the same reason — once, above the router outlet.
 * Every gated route mounts its own RequireAuth, so publishing the session from
 * inside the gate would tear it down and rebuild it on every navigation, and
 * the role-gated nav items would blink out and back each time. It also has to
 * outrank the gate because /e/$slug does not use RequireAuth at all.
 *
 * The push registration sits under the session for the same reason: one per
 * page load, shared by the register button, the inbox row and the Profile page,
 * and not rebuilt (with another token write) on every navigation.
 *
 * Toasts take pointer events themselves. An open Radix dialog or sheet sets
 * `pointer-events: none` on <body>, which the Toaster inherits, so without
 * this the Undo on a notification deleted from the bell could not be clicked
 * while the bell is open.
 */
export const Route = createRootRoute({
  component: () => (
    <SessionBoundary>
      <ThemeController />
      <PushForSession>
        <Outlet />
      </PushForSession>
      <AppToaster />
      <ForegroundPushToasts />
    </SessionBoundary>
  ),
});

function PushForSession({ children }: { children: ReactNode }) {
  const session = useSession();
  return (
    <PushRegistrationProvider uid={session?.uid ?? null}>
      {children}
    </PushRegistrationProvider>
  );
}
